// Design-token :root custom properties. Imported here, once, on purpose: this
// is emitted CSS, and vite.config.mjs injects scss/variables.scss into every
// component style block as a separate Sass compilation, so @forward-ing it
// would repeat the whole block once per component (DCTL-2252). The SCSS
// variables are forwarded from variables.scss as usual; only the emitted half
// lives here.
import "./scss/tokens-root.scss";
// Same reasoning, same layer: emitted CSS, imported once. Overrides Vuetify's own
// `.text-secondary` on field internals, which is why it cannot live in a
// component's scoped block.
import "./scss/field-chrome.scss";
// Same reasoning, same layer. Companions to DCTL-2288's `border-opacity: 1`: the
// four Vuetify components that read --v-border-opacity as a bare opacity MASK
// rather than as a border alpha, and so would render fully opaque after the flip.
import "./scss/border-opacity-masks.scss";
// Same reasoning, same layer: the shared always-drawn scrollbar of the QC wide
// grids (`.drawn-scrollbar`); pseudo-element rules one scoped block cannot share.
import "./scss/drawn-scrollbar.scss";
// Same reasoning, same layer: QC tag-row actions revealed on row hover or
// focus (`.qc-reveal-row` / `.qc-row-reveal`), shared across the cards.
import "./scss/qc-row-reveal.scss";

import { createApp } from "vue";
import { LDPlugin, LD_INIT } from "launchdarkly-vue-client-sdk";
import * as Sentry from "@sentry/vue";
import axios from "axios";
import auth from "./auth";
import CookieUtils from "@/utils/cookies";
import { installInflightTracking } from "@/utils/inflightRequests";
import {
	hasRoutePermissions,
	routePermissionsAllowed,
} from "@/utils/routePermissions";
import { ALL_PERMISSIONS } from "@/permissions";
import { PROSPECT_HOME_PATH } from "@/constants";

import vuetify from "./plugins/vuetify";
import "vuetify/styles";

import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import router from "./router";
import { useMainStore } from "./store";

// main component to render
import App from "./App.vue";

(async () => {
	const app = createApp(App);

	// Pinia must be installed before the first useMainStore() call (the router
	// guard below and the LaunchDarkly identify at the end both use the store).
	const pinia = createPinia();
	pinia.use(piniaPluginPersistedstate);
	app.use(pinia);
	const store = useMainStore();
	// Expose the store for e2e introspection (Playwright reads it off the app's
	// globalProperties, the way it previously read Vuex's $store).
	app.config.globalProperties.$mainStore = store;

	// launchdarkly
	app.use(LDPlugin, {
		clientSideID: import.meta.env.VUE_APP_LD_CLIENT_SIDE_ID,
		deferInitialization: true,
		options: {
			bootstrap: "localStorage",
		},
	});

	// set baseURL
	const protocol =
		import.meta.env.VUE_APP_ENV === "development" ? "http" : "https";
	const apiHost = import.meta.env.VUE_APP_API_HOST;
	const apiPort = import.meta.env.VUE_APP_API_PORT
		? `:${import.meta.env.VUE_APP_API_PORT}`
		: "";
	let apiUrl = `${protocol}://${apiHost}${apiPort}`;
	axios.defaults.baseURL = apiUrl;
	axios.defaults.withCredentials = true;

	// make axios available everywhere
	app.config.globalProperties.$http = axios;

	// track in-flight requests so logout can abort them before its own POST
	installInflightTracking(axios);

	// migrate cookies if needed (one-time cleanup)
	CookieUtils.migrateAutoLoginCookies();

	// detect if we're in an iframe
	const isInIframe = CookieUtils.isInIframe();
	if (isInIframe) {
		console.log(
			"[Dactyl] Running in iframe context - cookie behavior may be affected",
		);
	}

	// Shown when a navigation is cancelled because the session could not be
	// rehydrated over the network (no 401, nothing persisted to fall back on).
	const SERVER_UNREACHABLE_NOTIFICATION = {
		message: "Could not reach the server. Reload the page to continue.",
		status: "error",
		persist: true,
	};

	// Reset on every hard (re)load since this closure is re-created. Gates a
	// once-per-load /users/me reconcile so impersonation state (not persisted) is
	// re-read after a reload; SPA navigations within a load skip it (DCTL-2184).
	let sessionReconciled = false;

	// set up route guards to check authentication
	router.beforeEach(async (to, from, next) => {
		const isAuthenticated = auth.isAuthenticated();
		store.setAuthenticated(isAuthenticated);

		// In iframe context, log authentication state for debugging
		if (isInIframe && !isAuthenticated) {
			console.log(
				"[Dactyl iframe] Not authenticated - cookies may be blocked in iframe",
			);
		}

		// unauthenticated
		if (!isAuthenticated) {
			// if landing on a guest page and user has auto-login enabled, go straight to SSO redirect
			const isGuestRoute = to.matched.some((record) => record.meta.guest);
			const isSsoRedirect = to.path === "/sso-redirect";
			const noSsoParam = to?.query?.nosso === "1";
			const hasToken = to?.query?.token;
			const autoLogin = CookieUtils.shouldAutoLogin() && !noSsoParam;
			const isDev = import.meta.env.VUE_APP_ENV === "development";
			if (isGuestRoute && !isSsoRedirect && autoLogin && !hasToken && !isDev) {
				return next({ path: "/sso-redirect", query: to.query });
			}

			// authentication required
			if (to.matched.some((record) => record.meta.requireAuth)) {
				// In iframe context, don't redirect to login to prevent infinite loops
				// The parent frame should handle authentication
				if (isInIframe) {
					console.warn(
						"[Dactyl iframe] Authentication required but running in iframe - not redirecting to login",
					);
					console.warn(
						"[Dactyl iframe] User must authenticate in parent window first",
					);
					// Stay on the current route or allow navigation if it's a direct navigation
					if (from.path === to.path) {
						return next(false);
					}
					// Allow the navigation to proceed anyway - the component will handle showing login
					return next();
				}

				// send to login and preserve intended target
				next({
					path: "/login",
					query: { redirect: to.fullPath },
				});
			}
			// authentication not required
			else if (to.matched.some((record) => record.meta.guest)) {
				next();
			}
			return;
		}

		// Authenticated per the cookie. Treat the API session as the source of
		// truth and rehydrate from /users/me when EITHER the store has no user
		// identity (cleared localStorage, partial logout, dev cross-stack cookie
		// bleed - DCTL-2100) OR we have not yet reconciled since this load. The
		// latter is required for impersonation (DCTL-2184): `impersonator` is
		// deliberately not persisted, so after a hard reload mid-impersonation the
		// persisted target uuid would otherwise short-circuit this fetch and the
		// banner + Exit control would silently never render. A dead session yields
		// 401 -> full logout.
		if (!store.user.uuid || !sessionReconciled) {
			try {
				const { data } = await axios.get("/users/me");
				// DCTL-2184: /users/me carries an `impersonator` sibling when an
				// admin is viewing as another user. Fold it in so setUser can drive
				// the impersonation banner; `?? null` keeps the key present (and
				// clears any prior value) for the common non-impersonating case.
				store.setUser({
					...data.payload.user,
					impersonator: data.payload.impersonator ?? null,
				});
				sessionReconciled = true;
			} catch (error) {
				if (error.response?.status === 401) {
					auth.clearAuthenticated();
					store.setAuthenticated(false);
					store.reset();
					return next({ path: "/login", query: { redirect: to.fullPath } });
				}
				console.error("Failed to hydrate user from session:", error);
				// If we already hold a persisted identity, proceed with it rather
				// than blocking the app on a transient (non-401) /users/me failure;
				// mark reconciled so we don't retry every navigation this load. Any
				// impersonation state stays as last known until the next good load.
				if (store.user.uuid) {
					sessionReconciled = true;
				} else {
					// the cancelled navigation leaves the current page on screen,
					// so say why instead of stranding the user there (DCTL-2654)
					store.showNotification(SERVER_UNREACHABLE_NOTIFICATION);
					return next(false);
				}
			}
		}

		// authenticated - fetch permissions unless we already hold THIS user's.
		// Keyed to the user (not merely "any permissions loaded") so a previous
		// session's map - notably an admin's all-true map that survived an
		// incomplete logout - can never leak into the next user; a same-user
		// reload still skips the fetch since permissionsUserUuid is persisted.
		const user = store.user;
		const needsPermissions = store.permissionsUserUuid !== user.uuid;

		if (user && user.uuid && needsPermissions) {
			try {
				const { data } = await axios.get(`/users/${user.uuid}/permissions`, {
					params: { permissions: ALL_PERMISSIONS },
				});
				store.setPermissions({
					isAdmin: data.isAdmin,
					isInternalXlsxReviewer: data.isInternalXlsxReviewer,
					canManageSpecialProjectFlags: data.canManageSpecialProjectFlags,
					isProspect: data.isProspect,
					permissions: data.permissions,
					userUuid: user.uuid,
				});
			} catch (error) {
				console.error("Failed to load user permissions:", error);
				if (error.response?.status === 401) {
					auth.clearAuthenticated();
					store.setAuthenticated(false);
					store.reset();
					return next({
						path: "/login",
						query: { redirect: to.fullPath },
					});
				}
				store.showNotification(SERVER_UNREACHABLE_NOTIFICATION);
				return next(false);
			}
		}

		// Frontend mirror of backend ProspectContainment (DCTL-1931): a user
		// whose only role is `prospect` can reach only the Provenance Viewer and
		// their getting-started page. Anything else (notably the /home dashboard,
		// which would 403 on every data call and render a broken shell) is
		// steered to getting-started. The backend still enforces access; this is
		// purely so a contained prospect never lands somewhere unusable.
		if (store.isProspect) {
			const prospectAllowed =
				to.path === PROSPECT_HOME_PATH ||
				to.path.startsWith("/provenance-viewer/");
			if (!prospectAllowed) {
				return next(PROSPECT_HOME_PATH);
			}
		}

		// check route permissions
		if (to.matched.some((record) => record.meta.requireAuth)) {
			// DCTL-2484: a route meta may carry `permissions` (AND) and/or
			// `anyPermissions` (OR), and the two COMPOSE - see
			// utils/routePermissions. `anyPermissions` was added rather than
			// switching this call to `canAny`, which would have loosened every
			// multi-slug `permissions` route in router.js at once.
			const record = to.matched.find((record) =>
				hasRoutePermissions(record.meta),
			);

			if (record) {
				const allowed = routePermissionsAllowed(store, record.meta);
				if (allowed) {
					next();
				} else {
					next("/access-denied");
				}
			} else {
				next();
			}
		}
		// authentication not required but user is authenticated
		else if (to.matched.some((record) => record.meta.guest)) {
			// send to dashboard instead of guest page
			next("/home");
		}
	});

	// only enabling sentry for production for now
	if (import.meta.env.VUE_APP_ENV === "production") {
		Sentry.init({
			app,
			release: import.meta.env.VUE_APP_VERSION,
			dsn: "https://5e5e8d34c700b4f18b79cc51f7cbf03d@o4508049027891200.ingest.us.sentry.io/4508049048993792",
			integrations: [
				Sentry.browserTracingIntegration({ router }),
				Sentry.replayIntegration({
					blockAllMedia: false,
					maskAllInputs: false,
					maskAllText: false,
					networkDetailAllowUrls: [`${apiUrl}/data-entry/`, `${apiUrl}/ping`],
				}),
				Sentry.replayCanvasIntegration(),
				Sentry.captureConsoleIntegration({
					levels: ["error", "warn"],
				}),
			],
			// Tracing
			tracesSampleRate: 1.0, //  Capture 100% of the transactions
			// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
			tracePropagationTargets: ["localhost", apiUrl],
			// Session Replay
			replaysSessionSampleRate: 1.0, // This sets the sample rate at 100%. You may want to change it to 10% while in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			// Reduce span sizes
			beforeSendSpan: (span) => {
				if (
					span.op === "ui.long-animation-frame" &&
					span.data["browser.script.invoker_type"] === "event-listener" &&
					span.data["code.filepath"] ===
						`${protocol}://${import.meta.env.VUE_APP_WEB_HOST}/modules/wpd/app/wpd.min.js`
				) {
					if (span.data["browser.script.invoker"].length > 100) {
						span.data["browser.script.invoker"] = "ImageOnLoad";
					}
				}
				return span;
			},
		});
	}

	// include dactyl version
	app.config.globalProperties.$version = import.meta.env.VUE_APP_VERSION;

	// initialize vue instance and render main component
	app.use(router);
	// app.use(Vuelidate);
	app.use(vuetify);

	const ldInit = app._context.provides[LD_INIT];
	const [, ldClient] = ldInit();
	await ldClient.waitForInitialization(5);

	// Expose ldClient as a global property for Options API components
	app.config.globalProperties.$ldClient = ldClient;

	// check feature flag for api/v1 prefix
	const useApiV1Prefix = ldClient.variation("api-v1-prefix", false);
	if (useApiV1Prefix) {
		axios.defaults.baseURL = `${apiUrl}/api/v1`;
	}

	// Identify returning user to LaunchDarkly (from persisted Pinia state)
	const storedUser = store.user;
	if (storedUser && storedUser.uuid) {
		ldClient.identify({
			kind: "user",
			key: storedUser.uuid,
			email: storedUser.email,
			firstName: storedUser.first_name,
			lastName: storedUser.last_name,
			name: `${storedUser.first_name} ${storedUser.last_name}`,
		});
	}

	app.mount("#app");
})();
